import { jsxs, jsx } from "react/jsx-runtime";
import { useColors } from "@inbody/core";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import { useMediaQuery, Dialog, Box, Stack, IconButton, Typography, DialogContent, DialogActions } from "@mui/material";
import { FormButton } from "../form/form-button/FormButton.js";
function renderAction(action, index, loading) {
  return /* @__PURE__ */ jsx(
    FormButton,
    {
      variant: action.variant === "outlined" ? "outline" : "secondary",
      onClick: action.onClick,
      startIcon: action.icon,
      label: action.label,
      disabled: loading || action.disabled
    },
    index
  );
}
function ModalBase({
  open,
  onClose,
  title,
  subtitle,
  titleIcon,
  onBack,
  children,
  actions,
  leftActions,
  footerNote,
  maxWidth = "mobileWide",
  fullWidth = true,
  paperWidth,
  paperHeight,
  loading = false,
  contentSx,
  actionsJustify = "flex-end",
  showCloseButton = true,
  headerAction,
  fullScreenOnMobile = false,
  disableEnforceFocus = false,
  disableClose = false
}) {
  const { gray } = useColors();
  const isMobile = useMediaQuery("(max-width:600px)");
  const fullScreen = fullScreenOnMobile && isMobile;
  const handleClose = (_event, reason) => {
    if (loading) return;
    if (disableClose && (reason === "backdropClick" || reason === "escapeKeyDown")) {
      return;
    }
    onClose();
  };
  return /* @__PURE__ */ jsxs(
    Dialog,
    {
      open,
      onClose: handleClose,
      maxWidth,
      fullWidth,
      fullScreen,
      disableEscapeKeyDown: disableClose,
      disableEnforceFocus,
      PaperProps: {
        sx: {
          borderRadius: fullScreen ? 0 : 2,
          overflow: "hidden",
          ...paperWidth && !fullScreen ? { width: paperWidth, maxWidth: "calc(100vw - 32px)" } : {},
          ...paperHeight && !fullScreen ? { height: paperHeight, maxHeight: "calc(100vh - 64px)" } : {}
        }
      },
      children: [
        /* @__PURE__ */ jsxs(
          Box,
          {
            sx: {
              flexShrink: 0,
              px: 2.5,
              py: 2,
              bgcolor: gray[50],
              borderBottom: `1px solid ${gray[200]}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            },
            children: [
              /* @__PURE__ */ jsxs(Stack, { direction: "row", spacing: 1, alignItems: "center", children: [
                onBack && /* @__PURE__ */ jsx(
                  IconButton,
                  {
                    onClick: onBack,
                    size: "small",
                    sx: {
                      width: 28,
                      height: 28,
                      mr: 0.5,
                      color: gray[500],
                      "&:hover": { color: gray[800] }
                    },
                    "aria-label": "뒤로",
                    disabled: loading,
                    children: /* @__PURE__ */ jsx(ArrowBackIcon, { fontSize: "small" })
                  }
                ),
                titleIcon,
                /* @__PURE__ */ jsxs(Box, { children: [
                  /* @__PURE__ */ jsx(
                    Typography,
                    {
                      variant: "body1",
                      sx: { fontWeight: 600, color: gray[700] },
                      children: title
                    }
                  ),
                  subtitle && /* @__PURE__ */ jsx(
                    Typography,
                    {
                      variant: "caption",
                      sx: { display: "block", color: gray[500], mt: 0.25 },
                      children: subtitle
                    }
                  )
                ] })
              ] }),
              /* @__PURE__ */ jsxs(Stack, { direction: "row", spacing: 1, alignItems: "center", children: [
                headerAction,
                showCloseButton && !disableClose && /* @__PURE__ */ jsx(
                  IconButton,
                  {
                    onClick: () => handleClose(),
                    size: "small",
                    sx: {
                      width: 28,
                      height: 28,
                      color: gray[400],
                      "&:hover": { color: gray[700] }
                    },
                    "aria-label": "닫기",
                    disabled: loading,
                    children: /* @__PURE__ */ jsx(CloseIcon, { fontSize: "small" })
                  }
                )
              ] })
            ]
          }
        ),
        /* @__PURE__ */ jsx(DialogContent, { sx: { px: 2.5, py: 2, ...contentSx }, children }),
        (actions && actions.length > 0 || leftActions && leftActions.length > 0 || !!footerNote) && /* @__PURE__ */ jsxs(
          DialogActions,
          {
            sx: {
              flexShrink: 0,
              px: 2.5,
              py: 1.5,
              borderTop: `1px solid ${gray[200]}`,
              justifyContent: "space-between",
              /*
                좁은 화면에서 문구와 버튼을 한 줄에 두면 버튼이 눌리기 어려울 만큼
                좁아진다 — 문구를 윗줄로 올리고 버튼은 제 폭을 지킨다.
              */
              ...isMobile && footerNote && {
                flexDirection: "column",
                alignItems: "stretch",
                gap: 1
              }
            },
            children: [
              /* @__PURE__ */ jsxs(
                Box,
                {
                  sx: {
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    // 문구가 길어도 버튼을 밀어내지 않는다.
                    minWidth: 0,
                    flex: footerNote && !isMobile ? 1 : "none",
                    mr: footerNote && !isMobile ? 1.5 : 0
                  },
                  children: [
                    leftActions == null ? void 0 : leftActions.map(
                      (action, index) => renderAction(action, index, loading)
                    ),
                    footerNote
                  ]
                }
              ),
              /* @__PURE__ */ jsx(
                Box,
                {
                  sx: {
                    display: "flex",
                    gap: 1,
                    flexShrink: 0,
                    justifyContent: actionsJustify
                  },
                  children: actions == null ? void 0 : actions.map(
                    (action, index) => renderAction(action, index, loading)
                  )
                }
              )
            ]
          }
        )
      ]
    }
  );
}
export {
  ModalBase
};
